import React, { useState } from 'react';
import Hero from '../../components/Hero/Hero';
import Pathfinder from '../../components/Pathfinder/Pathfinder';
import Section from '../../components/Section/Section';
import Card from '../../components/Card/Card';
import GlobalCallout from '../../components/Global/CalloutDefault';
import { Box, Typography } from '@mui/material';
import Wallpaper from '../../components/Wallpaper/Wallpaper';
import Accordion from '../../components/Accordion/Accordion';
import Metadata from '../../components/Metadata/Metadata';
import Button from '../../components/Button/Button';
import Image from '../../components/Image/Image';
import ContentSplit from '../../components/ContentSplit/ContentSplit';
import ListItem from '../../components/ListItem/ListItem';
import { Link } from 'react-router-dom';

const PageContent: React.FC = () => {
  const [expandAll, setExpandAll] = useState(false);

  const toggleAll = () => {
    setExpandAll(!expandAll);
  };

  return (
    <Box>
      <Metadata
        title="Data Governance & Compliance | General Genomics"
        description="Learn how General Genomics ensures data security and regulatory compliance, including HIPAA and GDPR. Discover trusted expertise for public and commercial sectors."
      />
      <Hero
        helper="Data Governance and Compliance"
        headline="Trusted data governance for regulatory and industry compliance"
      />

      <Section theme="trust" className="cards-large-icons">
        <h2 className="action-on-view action-fade-in">
          Protect Your Data with Confidence
        </h2>
        <p className="action-on-view action-fade-in">
          At General Genomics, we prioritize safeguarding your sensitive
          information. Our data governance solutions ensure compliance with
          HIPAA, GDPR, and other global standards, providing robust security for
          public and commercial sectors. From risk management to secure
          processes, we simplify compliance, allowing you to focus on
          innovation. Contact us today to see how we turn complex regulations
          into reliable protection, tailored to your needs.
        </p>
        <Image
          path="/images/library/office-startup-10.webp"
          size="wide"
          className="action-on-view action-fade-in"
          alt="This is a wide image."
        />
      </Section>

      <ContentSplit
        image="/images/library/military-mechanics-man-woman.webp"
        position="right"
      >
        <p className="helper action-on-view action-fade-in">How It Works</p>
        <h2 className="action-on-view action-fade-in">
          Our Commitment to Compliance
        </h2>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Comprehensive Security Measures:</strong> Our systems
            protect sensitive data with multi-layer encryption and real-time
            monitoring.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Regulatory Adherence:</strong> Stay ahead with full
            compliance for HIPAA, GDPR, and government protocols.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Proven Expertise:</strong> Trusted by public health and
            private sectors to manage critical data securely.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Tailored Solutions:</strong> Custom strategies designed to
            meet your specific industry and compliance needs.
          </p>
        </ListItem>
      </ContentSplit>

      <Section theme="dark" className="section-cross-linking">
        <h2 className="action-on-view action-fade-in">
          Explore More About General Genomics
        </h2>
        <p className="action-on-view action-fade-in">
          Dive deeper into General Genomics&apos; offerings with these key areas
          that enhance compliance, security, and innovation.
        </p>
        <div className="card-group">
          <Card
            icon="settings"
            headline="Our Capabilities"
            subhead="Learn how data governance enhances our genomic solutions."
            btnURL="/our-approach/our-capabilities"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
          <Card
            icon="business"
            headline="Government Public Health"
            subhead="See how we apply compliance in sensitive government sectors."
            btnURL="/industries/government/public-health-and-policy"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
          <Card
            icon="data_usage"
            headline="Synthetic & </br>De-identified Data"
            subhead="Discover secure management of synthetic and de-identified data."
            btnURL="/data-portal"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
        </div>
      </Section>

      <Wallpaper asset="../images/library/government-building-data-4.webp"></Wallpaper>

      <Section>
        <Typography variant="h2" className="action-on-view action-fade-in">
          Frequently asked questions about data governance at General Genomics
        </Typography>
        <Typography className="action-on-view action-fade-in">
          Discover how General Genomics ensures data security, regulatory
          compliance, and trust through HIPAA, GDPR, and global standards.
        </Typography>
        <Box className="accordion-wrapper action-on-view action-fade-in">
          <Button
            className="accordion-button"
            type="outlined"
            onClick={toggleAll}
            icon={expandAll ? 'expand_less' : 'expand_more'}
            label={expandAll ? 'Collapse All' : 'Expand All'}
          />
          <Accordion
            isOpen={expandAll}
            headline=" What is General Genomics’ approach to data governance?"
          >
            General Genomics employs secure processes, rigorous risk management,
            and adherence to global standards like HIPAA and GDPR to protect
            sensitive data.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="How does General Genomics ensure compliance with regulations?"
          >
            We maintain compliance with frameworks such as HIPAA, GDPR, and
            government-specific protocols, ensuring data security for public and
            commercial clients.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="What industries does General Genomics serve with its secure data solutions?"
          >
            We specialize in supporting public sector organizations, including{' '}
            <Link to="/industries/government/public-health-and-policy">
              government public health
            </Link>{' '}
            and <Link to="/industries/commercial">commercial</Link> clients with
            tailored, compliant data services.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="How does General Genomics manage data privacy?"
          >
            Data privacy is maintained through advanced de-identification and
            synthetic data (SYN46™) solutions, securely managed within
            established compliance frameworks.
          </Accordion>
        </Box>
      </Section>

      <Section theme="dark" bgName="lines" className="section-call-to-action">
        <Typography variant="h2" className="action-on-view action-fade-in">
          Why partnering with General Genomics transforms bioinformatics
          research and innovation
        </Typography>
        <Box className="card-group">
          <Card
            icon="check"
            headline="Advanced Solutions in Analytics"
            subhead="General Genomics combines analytics and innovation with cutting-edge algorithms, large language models, machine learning, and predictive analytics to transform synthetic systems and complex data insights."
            className="action-on-view action-fade-in"
          />
          <Card
            icon="check"
            headline="Precision Insights"
            subhead="Harness predictive modeling and genomic data analysis to drive accurate, actionable insights tailored to research and healthcare objectives."
            className="action-on-view action-fade-in action-delay-250"
          />
          <Card
            icon="check"
            headline="Collaborative Expertise"
            subhead="Partner with a team experienced in supporting government initiatives to advance bioinformatics research and public health strategies."
            className="action-on-view action-fade-in action-delay-500"
          />
        </Box>
      </Section>

      <GlobalCallout />

      <Pathfinder />
    </Box>
  );
};

export default PageContent;
